import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getIsAuthenticated, getUserInfo } from "../../store/auth/selectors";
import { useSelector, useDispatch } from "react-redux";
import DeepLinkLogin from "./DeepLinkLogin";
import { DeepLinkOtp } from "./DeepLinkOtp";
import { useEventsMutation } from "../../store/ApiMutaion";
import { setSelectedEventsDetails, userLogout } from "../../store/auth/auth.slice";
import { setExamStatus } from "../../store/app/app.slice";
import { store } from "../../store";

const isOfflineEvent = (rawEvent) => rawEvent?.event_details?.event_type?.toLowerCase() === "offline";

const isExamTypeCompatibleWithStudent = (eventExamName, studentExam) => {
  const normalizedEventExam = eventExamName?.toLowerCase().replace(/\s+/g, "");
  const normalizedStudentExam = studentExam?.toLowerCase().replace(/\s+/g, "");
  const isJeeNeet = normalizedEventExam?.includes("jee") || normalizedEventExam?.includes("neet");

  if (!isJeeNeet) return true;

  return (
    normalizedStudentExam?.includes(normalizedEventExam) || normalizedEventExam?.includes(normalizedStudentExam)
  );
};

const isDeeplinkEventEligibleForExamPage = (rawEvent, studentExam) => {
  if (!rawEvent?.event_details) return false;
  if (isOfflineEvent(rawEvent)) return false;
  return isExamTypeCompatibleWithStudent(rawEvent.event_details?.exam_name, studentExam);
};

const DeeplinkLoginManager = ({ toggleTAndC, togglePAndP, deeplinkParams, path, onLoginComplete }) => {
  const isAuthenticated = useSelector(getIsAuthenticated);
  const user = useSelector(getUserInfo);
  const [otpLogin, setOtpLogin] = useState(false);
  const [fetchEvents] = useEventsMutation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasFetchedEvent, setHasFetchedEvent] = useState(false);
  const [forceLoggedOut, setForceLoggedOut] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Force logout on mount to ensure clean login state
  useEffect(() => {
    const performForceLogout = async () => {
      if (!forceLoggedOut) {
        await dispatch(userLogout());
        setForceLoggedOut(true);
        setIsReady(true);
      }
    };

    performForceLogout();
  }, [dispatch, forceLoggedOut]);

  const mapRawEventForDeeplink = (value, exam_status, source, studentExam) => {
    let finalExamId = value?.event_details?.exam_id;

    if ((exam_status === "completed" || path === "reports") && !finalExamId) {
      finalExamId = value?.exam_id || value?.event_details?.exam_id || value?.id;
    }

    return {
      title: value?.event_details?.name,
      url: value?.event_details?.provider_metadata?.event_link,
      startRecur: value?.event_details?.start_date_time,
      endRecur: value?.event_details?.end_date_time,
      exam_id: finalExamId,
      event_provider: value?.event_details?.provider_metadata?.event_provider,
      exam_attempted: value?.exam_attempted,
      crn_id: value?.crn_id,
      event_id: value?.event_id,
      event_code: value?.event_details?.event_code,
      grade_id: value?.student_details?.grade_id,
      level: value?.event_details?.level,
      examType: value?.event_details?.exam_name,
      StudentExamType: studentExam,
      qu_id: value?.event_details?.question_paper_id,
      round: value?.event_details?.round,
      submitedAt: value?.exam_attempted_at,
      is_paid_report: value?.event_details?.is_paid_report,
      event_type: value?.event_details?.event_type,
      event_date: value?.event_details?.start_date_time,
      is_exam_status: value?.exam_status,
      source: source,
      is_demo_booked: value?.is_demo_booked,
      certificate_link: value?.student_report?.certificate
    };
  };

  const resolveDeeplinkRawEvent = (responseData, crnIdFromUrl, eventIdFromUrl, exam_status) => {
    let eventArray;

    switch (exam_status) {
      case "missed":
        eventArray = responseData?.past_events?.unattempted_events;
        break;
      case "completed":
        eventArray = responseData?.past_events?.attempted_events;
        break;
      case "scheduled":
      default:
        eventArray = responseData?.scheduled_events;
        break;
    }

    const fromApi = eventArray?.find(
      (event) => String(event?.crn_id) === String(crnIdFromUrl) && String(event?.event_id) === String(eventIdFromUrl)
    );
    if (fromApi) return fromApi;

    const latestMappings = store.getState().auth.createdEventMappings;
    return (
      latestMappings?.find(
        (mapping) =>
          String(mapping?.crn_id) === String(crnIdFromUrl) && String(mapping?.event_id) === String(eventIdFromUrl)
      ) || null
    );
  };

  const fetchEvent = async (crnIdFromUrl, eventIdFromUrl, exam_status) => {
    if (hasFetchedEvent) return;
    let mappedEvents;
    try {
      const res = await fetchEvents();
      if (!res?.data?.error) {
        let source;

        switch (exam_status) {
          case "missed":
            source = "past_unattempted";
            break;
          case "completed":
            source = "past_attempted";
            break;
          case "scheduled":
          default:
            source = "scheduled";
            break;
        }

        const latestUser = store.getState().auth.user;
        const rawEvent = resolveDeeplinkRawEvent(res?.data, crnIdFromUrl, eventIdFromUrl, exam_status);

        if (!rawEvent || !isDeeplinkEventEligibleForExamPage(rawEvent, latestUser?.exam)) {
          history.push(`/dashboard`);
          setHasFetchedEvent(true);
          return undefined;
        }

        mappedEvents = [mapRawEventForDeeplink(rawEvent, exam_status, source, latestUser?.exam)];
        dispatch(setSelectedEventsDetails(mappedEvents[0]));
        setHasFetchedEvent(true);
      } else {
        console.error("Error:", res.error);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    }
    return mappedEvents;
  };

  const landOnExampage = async (crnIdFromUrl, eventIdFromUrl, exam_status) => {
    if (crnIdFromUrl && eventIdFromUrl && !hasFetchedEvent && exam_status) {
      const res = await fetchEvent(crnIdFromUrl, eventIdFromUrl, exam_status);
      if (res) {
        history.push(`/exam-page`);
      } else {
        history.push(`/dashboard`);
      }
    } else {
      history.push(`/dashboard`);
    }
  };

  const landOnReportspage = async (crnIdFromUrl, eventIdFromUrl, exam_status) => {
    if (crnIdFromUrl && eventIdFromUrl && !hasFetchedEvent && exam_status) {
      const res = await fetchEvent(crnIdFromUrl, eventIdFromUrl, exam_status);
      if (res) {
        if (res[0]?.event_provider === "CAREERCOUNSELLING") {
          history.push(`/career_reports`);
        } else {
          history.push(`/reports/${res[0]?.exam_id}`);
        }
      } else {
        history.push(`/dashboard`);
      }
    } else {
      history.push(`/dashboard`);
    }
  };

  const handlePostLoginNavigation = async () => {
    if (user?.is_active) {
      if (path === "dashboard") {
        dispatch(setExamStatus(deeplinkParams?.exam_status));
        history.push(`/dashboard`);
      } else if (path === "exampage") {
        landOnExampage(deeplinkParams?.crnId, deeplinkParams?.eventId, deeplinkParams?.exam_status);
      } else if (path === "reports") {
        const exam_status = "completed";
        landOnReportspage(deeplinkParams?.crnId, deeplinkParams?.eventId, exam_status);
      }
      sessionStorage.removeItem("deeplinkParams");
      onLoginComplete?.();
    } else {
      history.push(`/student/onboarding`);
    }
  };

  // Handle post-login navigation when authenticated after force logout
  useEffect(() => {
    if (forceLoggedOut && isAuthenticated) {
      handlePostLoginNavigation();
    }
  }, [isAuthenticated, forceLoggedOut]);

  const handleToggleLogins = () => {
    setOtpLogin(!otpLogin);
  };

  if (!isReady) {
    return null;
  }

  return (
    <div
      className="membership__card2"
      style={{
        backgroundColor: "radial-gradient(96.91% 96.91% at 96.41% 2.3%, rgb(15 63 213) 0%, rgb(3 14 51) 100%)"
      }}
    >
      <h3 className="tab-heading">Student Login</h3>
      {!otpLogin ? (
        <DeepLinkLogin
          toggleTAndC={toggleTAndC}
          togglePAndP={togglePAndP}
          loginIdFromParent={deeplinkParams?.id}
          passwordFromParent={deeplinkParams?.password}
          handleToggleLogins={handleToggleLogins}
          utmSource={deeplinkParams?.utm_source}
        />
      ) : (
        <DeepLinkOtp
          toggleTAndC={toggleTAndC}
          togglePAndP={togglePAndP}
          handleToggleLogins={handleToggleLogins}
          utmSource={deeplinkParams?.utm_source}
        />
      )}
    </div>
  );
};

export default DeeplinkLoginManager;
